
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ISorter, IFilter } from "@/models/tables";
import { DefaultFilters } from "@/data/filters/childAccounts";
import { DefaultSorters } from "@/data/sorters/childAccounts";

// Types
import {
  IChildAccountsProviderData,
  IChildAccountsProviderMethods
} from "@/models/providers/childAccounts";
import { IChildAccount, IClient } from "@/models/clients";

@Component
export default class ChildAccountsProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: `CAP1` /** [C]hild  [A]ccounts [P]rovider [1] */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  @Prop({ required: true })
  public parentClient!: IClient;

  // Data

  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public hasRequestError = false;

  public dataModule = DataModules.CLIENTS_CHILD_ACCOUNTS;

  public filters = this.availableFilters;
  public sorters = this.availableSorters;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.dataPayloadSettings = { id: this.parentClient?.id };
    this.getData();

    this.$bus.$on("refreshChildAccountListing", this.reloadData);
  }

  public beforeDestroy() {
    this.$bus.$off("refreshChildAccountListing");
  }

  // Methods

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public getStoreParams(params: any): Promise<any> {
    const defaultParams = {
      with: "child_client,child_client.image"
    };
    return _.merge({ params: defaultParams }, params);
  }

  public updateChildRelation(childAccount: IChildAccount) {
    if (!this.canUpdate) return Promise.resolve();
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_CHILD_ACCOUNTS}/openManageChildAccountModal`,
      {
        parent: this,
        props: {
          childAccount: childAccount,
          childClient: childAccount.child_client,
          isParent: true,
          parentClient: this.parentClient
        },
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public detachChildAccount(configId: IChildAccount["id"]) {
    if (!this.canDetach) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_CHILD_ACCOUNTS}/detachChildAccount`,
      {
        parentId: this.parentClient?.id,
        configId,
        onSuccess: () => {
          this.reloadData();
          this.$store.dispatch("toast/show", {
            message: this.$t(
              "_sentence.related_accounts.child_account_successfully_detached"
            )
          });
        }
      }
    );
  }

  public impersonateChild({
    child_client_id,
    allow_impersonation
  }: IChildAccount) {
    if (!child_client_id || !allow_impersonation) return Promise.resolve();
    this.isProcessing = true;
    return this.$store
      .dispatch("auth/client/impersonateChild", child_client_id)
      .finally(() => {
        this.isProcessing = false;
      });
  }

  // Computed

  public get canShowMore(): boolean {
    return this.table.total > this.table.data.length;
  }

  // Guarding

  public get canDetach() {
    return this.$userCan("delete_child_client_configs");
  }

  public get canUpdate() {
    return this.$userCan("update_child_client_configs");
  }

  public guardFunctionalities = ["list_child_client_configs"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("childAccountsProviderData")
  public providerData(): IChildAccountsProviderData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      canDetach: this.canDetach,
      canShowMore: this.canShowMore,
      canUpdate: this.canUpdate,
      childAccounts: this.table.data as IChildAccount[],
      constQueryFilters: this.constQueryFilters,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.hasRequestError,
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("childAccountsProviderMethods")
  public get providerMethods(): IChildAccountsProviderMethods {
    return {
      detachChildAccount: this.detachChildAccount,
      getData: this.getData,
      impersonateChild: this.impersonateChild,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      updateChildRelation: this.updateChildRelation
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $childAccountsData: this.providerData(),
        $childAccountsMethods: this.providerMethods
      })
    );
  }
}
